// BaseMemberService.js

/**
 * BaseMemberService.js
 *
 * Service for reading and managing space members.
 */

import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { collections, db, functions, httpsCallable } from 'firebaseConfig';
import { getPersonEmail, getPersonName } from 'utilities/PersonIdentity';
import {
  dualDeleteBaseMemberClient,
  dualSetBaseMemberClient,
  dualUpdateBaseMemberClient,
} from 'utilities/baseDualWrite';
import {withBaseKey, buildTenantCallableFields} from 'utilities/basePersistence';
import { buildUserSummary } from 'utilities/Users';

const BASES_COLLECTION = collections.bases;
const callable = (name) => httpsCallable(functions, name);

function isPlainObject(value) {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== undefined)
      .map((entry) => stripUndefinedDeep(entry));
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (entryValue === undefined) {
      return accumulator;
    }

    accumulator[key] = stripUndefinedDeep(entryValue);
    return accumulator;
  }, {});
}

function membersDatabase(baseKey) {
  return collection(db, BASES_COLLECTION, baseKey, 'members');
}

function memberDocument(baseKey, userKey) {
  return doc(db, BASES_COLLECTION, baseKey, 'members', userKey);
}

export function subscribeToBaseMembers(baseKey, callback, onError) {
  if (!baseKey || typeof callback !== 'function') {
    return () => {};
  }

  const q = query(membersDatabase(baseKey));
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      callback(rows);
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[BaseMemberService] subscribeToBaseMembers error:', error);
      callback([]);
    }
  );
}

export function subscribeToBaseMember(baseKey, userKey, callback, onError) {
  const normalizedBaseKey = String(baseKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedBaseKey || !normalizedUserKey || typeof callback !== 'function') {
    return () => {};
  }

  return onSnapshot(
    memberDocument(normalizedBaseKey, normalizedUserKey),
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      callback({
        id: snapshot.id,
        ...(snapshot.data() || {}),
      });
    },
    (error) => {
      if (typeof onError === 'function') onError(error);
      else console.error('[BaseMemberService] subscribeToBaseMember error:', error);
      callback(null);
    }
  );
}

export async function upsertBaseMember(baseKey, userNode, role = 'member') {
  if (!baseKey || !userNode?.key) {
    throw new Error('baseKey and user key are required');
  }

  const now = serverTimestamp();
  const userKey = userNode.key;
  const userSummary = buildUserSummary(userNode);

  await dualSetBaseMemberClient(
    baseKey,
    userKey,
    withBaseKey({
      key: userKey,
      userKey,
      role,
      user: userSummary,
      title: userSummary?.name || getPersonName(userNode),
      email: userSummary?.email || getPersonEmail(userNode),
      dateModified: now,
      dateCreated: now,
    }, baseKey),
    { merge: true }
  );
}

export async function setBaseMemberRole(baseKey, userKey, role) {
  if (!baseKey || !userKey || !role) {
    throw new Error('baseKey, userKey, and role are required');
  }

  const fn = callable('setBaseMemberRole');
  await fn({ ...buildTenantCallableFields(baseKey), userKey, role });
}

export async function removeBaseMember(baseKey, userKey) {
  if (!baseKey || !userKey) {
    throw new Error('baseKey and userKey are required');
  }

  // Firestore rules enforce members.remove / members.leaveBase; deleting here
  // still runs onBaseMemberDeleted for seat count and billing sync.
  await dualDeleteBaseMemberClient(baseKey, userKey);
}

export async function updateBaseMemberProfileData(baseKey, userKey, profileData) {
  const normalizedBaseKey = String(baseKey || '').trim();
  const normalizedUserKey = String(userKey || '').trim();

  if (!normalizedBaseKey || !normalizedUserKey) {
    throw new Error('baseKey and userKey are required');
  }

  const normalizedProfileData = profileData && typeof profileData === 'object'
    ? stripUndefinedDeep(profileData)
    : {};

  await dualUpdateBaseMemberClient(normalizedBaseKey, normalizedUserKey, {
    data_member_profile: normalizedProfileData,
    dateModified: serverTimestamp(),
  });
}
